import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Users, CheckCircle, XCircle, LogIn, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JoinTeam = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [invitationValid, setInvitationValid] = useState(null);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const invitationToken = searchParams.get('token');

  useEffect(() => {
    if (invitationToken) {
      validateInvitation();
    } else {
      setLoading(false);
      setError('No invitation token provided');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationToken]);

  const validateInvitation = async () => {
    try {
      const response = await axios.get(`${API}/teams/invitations/validate?invitation_token=${invitationToken}`);
      
      if (response.data.valid) {
        setInvitationValid(true);
        setInvitationInfo(response.data);
      } else {
        setInvitationValid(false);
        setError(response.data.error || 'Invalid invitation');
      }
    } catch (err) {
      setInvitationValid(false);
      setError(err.response?.data?.detail || 'Failed to validate invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTeam = async () => {
    if (!token) {
      // User needs to login first - save invitation token and redirect
      sessionStorage.setItem('pendingInvitation', invitationToken);
      navigate('/login', { state: { returnTo: `/join-team?token=${invitationToken}` } });
      return;
    }

    setJoining(true);
    try {
      const response = await axios.post(`${API}/teams/invitations/accept?token=${token}`, {
        invitation_token: invitationToken
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success('Successfully joined the team!');
        setTimeout(() => navigate('/team'), 2000);
      } else {
        setError(response.data.error || 'Failed to join team');
        toast.error(response.data.error || 'Failed to join team');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to join team';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
          <p className="text-slate-500">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" data-testid="join-success">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold text-primary mb-2">Welcome to the Team!</h2>
            <p className="text-slate-500 mb-6">
              You've successfully joined {invitationInfo?.team_name || 'the team'}.
            </p>
            <p className="text-sm text-slate-400">Redirecting to team dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitationValid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center" data-testid="join-error">
          <CardContent className="pt-8 pb-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-semibold text-primary mb-2">Invalid Invitation</h2>
            <p className="text-slate-500 mb-6">
              {error || 'This invitation link is invalid or has expired.'}
            </p>
            <Link to="/login">
              <Button>Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="join-team-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-accent" />
          </div>
          <CardTitle className="text-2xl">You're Invited!</CardTitle>
          <CardDescription>
            You've been invited to join a team on Treppan CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-slate-500">Team</span>
              <span className="font-medium">{invitationInfo?.team_name || 'Team'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Your Role</span>
              <span className="font-medium capitalize">{invitationInfo?.role?.replace('_', ' ') || 'Member'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Invited By</span>
              <span className="font-medium">{invitationInfo?.invited_by_name || 'Team Owner'}</span>
            </div>
          </div>

          {!user ? (
            <div className="space-y-3">
              <p className="text-sm text-center text-slate-500">
                Please log in or create an account to join this team
              </p>
              <Button 
                onClick={handleJoinTeam} 
                className="w-full bg-accent hover:bg-accent/90"
                data-testid="login-to-join-btn"
              >
                <LogIn className="mr-2" size={16} />
                Log In to Join
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-center text-slate-500">
                Logged in as <span className="font-medium">{user.email}</span>
              </p>
              <Button 
                onClick={handleJoinTeam} 
                disabled={joining}
                className="w-full bg-accent hover:bg-accent/90"
                data-testid="join-team-btn"
              >
                {joining ? (
                  <>
                    <RefreshCw className="mr-2 animate-spin" size={16} />
                    Joining...
                  </>
                ) : (
                  <>
                    <Users className="mr-2" size={16} />
                    Join Team
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default JoinTeam;
